import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createUseStyles } from "react-jss";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import {
  clampKenBurnsFocus,
  createCoverDragOrigin,
  computeCoverFocusPoint,
} from "../background/kenBurnsCalculator";
import { WysiwygKenBurnsHud } from "./WysiwygKenBurnsHud";
import { KenBurnsModeBanner } from "./KenBurnsModeBanner";

export const resolveImageSrc = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return "";
  if (
    imageUrl.startsWith("http://") ||
    imageUrl.startsWith("https://") ||
    imageUrl.startsWith("/") ||
    imageUrl.startsWith("data:")
  ) {
    return imageUrl;
  }
  return `https://ems.dimu.org/image/${imageUrl}?dimension=1200x1200`;
};

export const findSectionBackgroundEl = (sectionId, containerId) => {
  const card =
    document.getElementById(`container-${sectionId}-${containerId}`) ||
    document.querySelector(`[id*='container-${sectionId}-']`);
  const sectionEl = card?.closest("section");
  if (sectionEl) {
    const stickyBg = sectionEl.querySelector("[class*='singleBackground']");
    if (stickyBg) return stickyBg;
    return sectionEl;
  }
  return (
    document.querySelector("[class*='singleBackground']") ||
    document.getElementById("scrollytell-content") ||
    document.body
  );
};

const useStyles = createUseStyles({
  overlay: {
    position: "fixed",
    zIndex: 1200,
    cursor: "crosshair",
    userSelect: "none",
    boxShadow: "inset 0 0 0 4px #4484F1, inset 0 0 28px rgba(68, 132, 241, 0.28)",
    backgroundColor: "rgba(15, 23, 42, 0.08)",
  },
  reticle: {
    position: "absolute",
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "2px solid #FFFFFF",
    boxShadow: "0 0 8px rgba(0,0,0,0.6), inset 0 0 4px rgba(0,0,0,0.6)",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&::before": {
      content: '""',
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#4484F1",
    },
  },
});

export const ImageFocusZoomPicker = ({
  imageUrl,
  x = 0,
  y = 0,
  zoom = 1.0,
  effect = "none",
  onFocusZoomChanged,
  onClose,
  sectionId,
  containerId,
  className = "",
}) => {
  const t = useScrollytellTranslation();
  const classes = useStyles();
  const hudRef = useRef(null);
  const dragOriginRef = useRef(null);
  const [currentFocus, setCurrentFocus] = useState({ x, y });
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [currentEffect, setCurrentEffect] = useState(effect || "none");
  const [isDragging, setIsDragging] = useState(false);
  const [bgRect, setBgRect] = useState(null);

  const updateBgRect = useCallback(() => {
    const el = findSectionBackgroundEl(sectionId, containerId);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setBgRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      }
    }
  }, [sectionId, containerId]);

  useEffect(() => {
    updateBgRect();
    window.addEventListener("resize", updateBgRect);
    window.addEventListener("scroll", updateBgRect);
    return () => {
      window.removeEventListener("resize", updateBgRect);
      window.removeEventListener("scroll", updateBgRect);
    };
  }, [updateBgRect]);

  useEffect(() => {
    const card =
      document.getElementById(`container-${sectionId}-${containerId}`) ||
      document.querySelector(`[id*='container-${sectionId}-']`);
    if (card) {
      const origOpacity = card.style.opacity;
      const origPointerEvents = card.style.pointerEvents;
      card.style.opacity = "0.25";
      card.style.pointerEvents = "none";
      return () => {
        card.style.opacity = origOpacity;
        card.style.pointerEvents = origPointerEvents;
      };
    }
  }, [sectionId, containerId]);

  const updateFromCoords = useCallback(
    (newX, newY, newZoom, newEffect) => {
      const effectToUse = newEffect !== undefined ? newEffect : currentEffect;
      const clamped = clampKenBurnsFocus({
        x: Number(newX.toFixed(2)),
        y: Number(newY.toFixed(2)),
        zoom: newZoom !== undefined ? newZoom : currentZoom,
        effect: effectToUse,
      });
      setCurrentFocus({ x: clamped.x, y: clamped.y });
      if (newZoom !== undefined) setCurrentZoom(clamped.zoom);
      if (newEffect !== undefined) setCurrentEffect(effectToUse);
      onFocusZoomChanged(clamped);
    },
    [currentZoom, currentEffect, onFocusZoomChanged]
  );

  const handleMouseDown = (e) => {
    if (hudRef.current && hudRef.current.contains(e.target)) return;
    setIsDragging(true);

    const bgEl = findSectionBackgroundEl(sectionId, containerId);
    const rect = bgEl ? bgEl.getBoundingClientRect() : null;
    const bgImg = bgEl?.querySelector("img[data-testid='background-highres-img']") || bgEl?.querySelector("img");
    const overlayRect = e.currentTarget?.getBoundingClientRect();

    const origin = createCoverDragOrigin({
      rect: rect && rect.width > 0 ? rect : overlayRect,
      natW: bgImg?.naturalWidth || 0,
      natH: bgImg?.naturalHeight || 0,
      currentFocus,
      currentZoom,
      fallbackWidth: overlayRect?.width || 1,
      fallbackHeight: overlayRect?.height || 1,
    });

    dragOriginRef.current = origin;
    const { rawX, rawY } = computeCoverFocusPoint(e.clientX, e.clientY, origin);
    updateFromCoords(rawX, rawY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragOriginRef.current) return;
    const { rawX, rawY } = computeCoverFocusPoint(e.clientX, e.clientY, dragOriginRef.current);
    updateFromCoords(rawX, rawY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragOriginRef.current = null;
    updateBgRect();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") { e.preventDefault(); updateFromCoords(currentFocus.x - 0.05, currentFocus.y); }
      if (e.key === "ArrowRight") { e.preventDefault(); updateFromCoords(currentFocus.x + 0.05, currentFocus.y); }
      if (e.key === "ArrowUp") { e.preventDefault(); updateFromCoords(currentFocus.x, currentFocus.y + 0.05); }
      if (e.key === "ArrowDown") { e.preventDefault(); updateFromCoords(currentFocus.x, currentFocus.y - 0.05); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentFocus, updateFromCoords, onClose]);

  const u = (currentFocus.x + 1) / 2;
  const v = (1 - currentFocus.y) / 2;
  const reticleStyle = { left: `${u * 100}%`, top: `${v * 100}%` };

  const overlayStyle =
    bgRect && bgRect.width > 0 && bgRect.height > 0
      ? { top: bgRect.top, left: bgRect.left, width: bgRect.width, height: bgRect.height }
      : { top: 0, left: 0, width: "100vw", height: "100vh" };

  const overlayContent = (
    <>
      <div
        className={`${classes.overlay} ${className}`}
        style={overlayStyle}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        role="dialog"
        aria-modal="true"
        aria-label={t("imageFocusZoomPicker.title", "WYSIWYG Ken Burns")}
        data-testid="wysiwyg-kenburns-overlay"
      >
        <KenBurnsModeBanner onClose={onClose} />
        <div className={classes.reticle} style={reticleStyle} data-testid="kenburns-reticle" />
      </div>
      <WysiwygKenBurnsHud
        hudRef={hudRef}
        imageUrl={imageUrl}
        currentFocus={currentFocus}
        currentZoom={currentZoom}
        currentEffect={currentEffect}
        onZoomChange={(_, val) => updateFromCoords(currentFocus.x, currentFocus.y, val)}
        onFocusClick={(nx, ny) => updateFromCoords(nx, ny)}
        onEffectChange={(newEff) => updateFromCoords(currentFocus.x, currentFocus.y, currentZoom, newEff)}
        onReset={() => updateFromCoords(0, 0, 1.0, "none")}
        onClose={onClose}
      />
    </>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(overlayContent, document.body)
    : overlayContent;
};
